import {flow, getParent, getRoot, getSnapshot, types} from 'mobx-state-tree'
import Router from 'next/router'
import {AES, enc} from "crypto-js"

import {
  PAYMENT_PROVIDERS,
  REQUEST_STATUSES,
  TRAFFIC_STATES_URL_SLUGS,
  TRAFFIC_STATUS_ORDER,
  TRAFFIC_UNEDITABLE_STATUS_VALUE,
} from '~/utilities/constants'

import * as LocalStorage from '~/utilities/localStorage'
import * as TrafficTicketService from '~/services/trafficTicket'
import * as TrafficRegionService from '~/services/trafficRegion'
import * as TrafficStateService from '~/services/trafficState'
import {chargePaymentPlanDownPayment, chargePaymentPlanDownPaymentACH, chargeTrafficTicket, overwritePaymentProcessor} from "~/services/payments"
import { trackMetaBrowserLead, trackMetaBrowserPurchase } from "~/utilities/metaCapiBrowserEvents"
import TRACKING from '~/services/tracking'

import Network from "~/stores/models/Network"
import {
  getSubmissionSteps,
  getFirstStep,
  getNextStep,
  getPreviousStep,
  getStepFromSlug
} from '~/components/traffic-tickets/steps/steps'
import {SCREEN_KEYS} from '~/components/traffic-tickets/screens'

import TrafficState from './models/TrafficState'
import TrafficTicket from './models/TrafficTicket'
import TrafficRegion from './models/TrafficRegion'
import TrafficCourt from './models/TrafficCourt'
import TrafficCounty from './models/TrafficCounty'
import MovingViolation from './models/MovingViolation'
import TrafficQuote from './models/TrafficQuote'

import TRACKING_STEP_NAMES from './trackingSteps'
import FinancingPlan from "~/stores/trafficTicket/models/FinancingPlan"

const sanitizeViolation = ({ displayName, ...rest }) => ({
  displayName: displayName.replaceAll("�", " "),
  ...rest,
})

const StepSlugs = getSubmissionSteps(true).map(item => item.slug)

export const TrafficSubmissionStore = types
  .model('TrafficSubmissionStore', {
    storeName: "submissionStore",
    statusText: types.maybeNull(types.string),
    // returning users trying complete a ticket
    targetObjectId: types.maybeNull(types.string),
    // Used by all uses
    trafficTickets: types.array(types.late(() => TrafficTicket)),
    trafficStates: types.array(TrafficState),
    trafficStatesStatus: types.optional(types.string, REQUEST_STATUSES.init),
    trafficRegions: types.array(TrafficRegion),
    trafficRegionsLoadedAll: types.optional(types.boolean, false),
    trafficRegionsStatus: types.optional(types.string, REQUEST_STATUSES.init),
    allTrafficCourts: types.array(TrafficCourt),
    allTrafficCounties: types.array(TrafficCounty),
    selectedState: types.frozen({}),
    currentStepErrors: types.array(types.string),
    isLoading: false,
    step: types.maybeNull(types.enumeration(StepSlugs)),
    initialized: false,
    mobileMidstepCompleted: false,
    screen: types.maybeNull(
      types.optional(
        types.enumeration(Object.keys(SCREEN_KEYS)),
        SCREEN_KEYS.initialize
      )
    ),
    movingViolations: types.array(MovingViolation),
    selectedMovingViolationId: types.maybeNull(types.string),
    selectedMovingViolationSlider: types.maybeNull(types.frozen({})),
    movingViolationService: types.optional(Network, {}),
    quotes: types.array(TrafficQuote),
    quotesStatus: types.optional(types.string, REQUEST_STATUSES.init),
    attorneyFakeStatus: types.optional(types.number, 1),
    selectedFinancingPlan: types.maybeNull(FinancingPlan, {}),
    isOneStepCheckout: false,
    isOneStepPaymentPlanCheckout: false,
    isWebMobile: false,
    isStateAffiliatedWithWinitLaw: types.optional(types.boolean, false)
  })
  .views(self => ({
    get fullStep() {
      return getStepFromSlug(self.step, self.selectedState.available && !self.selectedState.isPQFlowEnabled)
    },
    get nextStepConfig() {
      return getNextStep(self.step, self.selectedState.available && !self.selectedState.isPQFlowEnabled)
    },
    get selectedRegion() {
      return self.trafficTickets[0]?.trafficRegion
    },
    get selectedViolation() {
      return self.movingViolations.find(violation => violation.objectId === self.selectedMovingViolationId)
    },
    get trafficTicket() {
      if (self.trafficTickets.length) {
        return self.trafficTickets[0]
      }
      return null
    },
    get trafficCourts() {
      if (self.selectedRegion) {
        return self.selectedRegion.trafficCourts
      }
      return self.allTrafficCourts
    },
    get trafficCounties() {
      if (self.selectedRegion) {
        return self.selectedRegion.trafficCounties
      }
      return self.allTrafficCounties
    },
    get suggestedMovingViolations() {
      return self.movingViolations.filter(v =>
        (v.isSuggested && v.winItName !== "TLC") ||
        (v.isSuggested && v.winItName === "TLC" && self.selectedState.shortName === "NY")
      )
    },
    get notSuggestedMovingViolations() {
      return self.movingViolations.filter(v => !v.isSuggested)
    },
    get totalPrice() {
      return self.trafficTickets.reduce((total, ticket) => {
        let displayPrice = total
        if (ticket.winItPrice) displayPrice += ticket.winItPrice
        if (ticket.caseProtectionActivated) {
          if (ticket.caseProtectionFee) displayPrice += ticket.caseProtectionFee
        }
        return displayPrice
      }, 0)
    }
  }))
  .actions(self => ({
    reset: () => {
      self.statusText = null
      self.targetObjectId = null
      self.trafficTickets = []
      self.trafficRegions = []
      self.trafficRegionsLoadedAll = false
      self.trafficRegionsStatus = REQUEST_STATUSES.init
      self.allTrafficCounties = []
      self.allTrafficCourts = []
      self.currentStepErrors = []
      self.selectedState = {
        shortName: 'NY',
        longName: 'New York',
        available: true,
        isPQFlowEnabled: false,
        prospect: true
      }
      self.isPQFlowEnabled = false
      self.initialized = false
      self.mobileMidstepCompleted = false
      self.step = null
      self.isLoading = false
      self.screen = null
      self.movingViolations = []
      self.selectedMovingViolationId = null
      self.selectedMovingViolationSlider = {}
      self.movingViolationService.requestReset()
      self.quotes = []
      self.quotesStatus = REQUEST_STATUSES.init
      self.attorneyFakeStatus = 1
      self.isOneStepCheckout = false
      self.isOneStepPaymentPlanCheckout = false
      self.isWebMobile = false
    },
    setIsStateAffiliatedWithWinitLaw(stateShortName) {
      try {
        if (stateShortName === 'CAL' || stateShortName === 'CAL_STATE') {
          stateShortName = 'CA'
        }
        const trafficState = self.trafficStates.filter(
            item => item.shortName === stateShortName
        )
        if (!trafficState)
          console.error('Error fetching traffic state for ', stateShortName)
        self.isStateAffiliatedWithWinitLaw =
            trafficState[0].affiliatedWithWinitLaw
      } catch (error) {
        console.error(
            'Error fetching affiliatedWithWinitLaw flag for state',
            stateShortName
        )
        self.isStateAffiliatedWithWinitLaw = false
      }
    },
    // Initializes the traffic submission store
    initialize: flow(function* initializeGen() {
      if (!self.initialized) {
        const rootStore = getParent(self)
        const authStore = rootStore.auth
        const { user } = authStore
        self.screen = SCREEN_KEYS.initialize
        yield self.loadTrafficStates()
        let targetStep = getFirstStep((self.selectedState.available && !self.selectedState.isPQFlowEnabled) || true)
        let loadedTicket = {}
        const { target: objectId } = Router.query

        // One-step checkout (from attorney portal link-generator):
        let queryParams = null
        const { oneStepCheckout, oneStepCheckoutForPaymentPlan, userFirstName, userLastName, userEmail, userPhoneNumber,  isWebMobile } = Router.query
        if (authStore.isLoggedIn) {
          if (oneStepCheckoutForPaymentPlan) {
            queryParams = JSON.parse(
                AES.decrypt(oneStepCheckoutForPaymentPlan, process.env.ENCRYPTION_KEY).toString(enc.Utf8)
            );

            if (queryParams.isACH) {
              rootStore.payment.setIsACH(true)
            }

            const { financingPlanId, locationState } = queryParams || {}
            // Navigate to PaymentPlanForm with query params
            if (financingPlanId && locationState) {
              console.log('Navigating to PaymentPlanForm')
              self.setSelectedState({value: locationState})
              if (user && user.paymentProcessor.type !== 'cko') {
                yield overwritePaymentProcessor(user, 'NJ').then(() => { // Force CKO whatever the state
                  user.changeKey('paymentProcessor', { type: PAYMENT_PROVIDERS.cko })
                })
              }
              try {
                // Use store action to fetch the financing plan
                yield self.fetchFinancingPlan(financingPlanId, user.sessionToken)
              } catch (error) {
                console.error("Failed to fetch financing plan:", error.message)
              }
              self.trafficTickets = self.selectedFinancingPlan.tickets.map(ticket => TrafficTicket.create((getSnapshot(ticket))))
              try {
                const attorneyQuotes = yield TrafficTicketService.getQuotes(self.trafficTickets[0], user.sessionToken)
                self.setQuotes(attorneyQuotes)
                self.trafficTickets[0].selectQuote(self.quotes[0])
                yield self.trafficTickets[0].fetchCaseProtection(user.sessionToken)
              } catch (error) {
                console.error("Failed to fetch quotes for tickets:", error.message)
              }
              if (self.selectedFinancingPlan.status !== "pending") {
                targetStep = getStepFromSlug("done", self.selectedState.available && !self.selectedState.isPQFlowEnabled)
              } else {
                targetStep = getStepFromSlug("payment-details", self.selectedState.available && !self.selectedState.isPQFlowEnabled)
              }
              self.setIsStateAffiliatedWithWinitLaw(self.trafficTickets[0].locationState)
              self.setIsOneStepPaymentPlanCheckout(true)
            }
          } else if (objectId) {
            self.targetObjectId = objectId
            self.isWebMobile = isWebMobile === 'true'
            try {
              self.changeStatusText("Loading Ticket")
              loadedTicket = yield TrafficTicketService.get(self.targetObjectId, user.sessionToken)

              if (loadedTicket?.locationState) {
                self.setSelectedState({value: loadedTicket.locationState})
                self.setIsStateAffiliatedWithWinitLaw(loadedTicket.locationState)
              }

              if (loadedTicket?.trafficRegion && loadedTicket.trafficRegion?.objectId) {
                self.changeStatusText("Loading Ticket Location Data")
                yield self.loadRegion(loadedTicket.trafficRegion.objectId)
              }

              const attorneyQuotes = yield TrafficTicketService.getQuotes(loadedTicket, user.sessionToken)
              self.setQuotes(attorneyQuotes)

              self.addTrafficTicket(loadedTicket, user.sessionToken)
              self.trafficTickets[0].selectQuote(self.quotes[0])
              yield self.trafficTickets[0].fetchCaseProtection(user.sessionToken)

              if (loadedTicket?.violationName) {
                yield self.loadViolations()
                const violation = self.movingViolations.find(movingViolation => movingViolation.displayName === loadedTicket.violationName)
                if (violation) {
                  self.setSelectedViolation({
                    value: violation.objectId,
                    label: violation.displayName,
                    points: String(violation.points),
                    slider: violation.slider
                  })
                }
              }

              if (loadedTicket.status) {
                const statusValue = TRAFFIC_STATUS_ORDER[loadedTicket.status]
                if (statusValue >= TRAFFIC_UNEDITABLE_STATUS_VALUE) {
                  self.setScreen(SCREEN_KEYS.alreadySubmitted)
                  self.changeStatusText(null)
                  targetStep = getStepFromSlug("done", self.selectedState.available && !self.selectedState.isPQFlowEnabled)
                } else {
                  if (user && user.paymentProcessor.type !== 'cko') {
                    yield overwritePaymentProcessor(user, loadedTicket.locationState).then(res => {
                      if (res.isAffiliatedWithWinitLaw) {
                        user.changeKey('paymentProcessor', {type: PAYMENT_PROVIDERS.cko})
                      }
                    })
                  }
                  targetStep = getStepFromSlug("payment-details", self.selectedState.available && !self.selectedState.isPQFlowEnabled)
                }
              }
            } catch (err) {
              console.error('Issue auto loading ticket', err)
              self.changeStatusText(null)
              self.targetObjectId = null
              self.showErrorToast(err)
            }
          }
        }

        if (!objectId) {
          const { slug } = Router.query
          const state = slug?.replace('-traffic-tickets', '') // get the state abbreviation by itself
          const stateShortName = TRAFFIC_STATES_URL_SLUGS[state]?.shortName || "NY"
          console.log(`Setting selected state to ${stateShortName}`)
          self.addTrafficTicket(loadedTicket, user.sessionToken)
          self.setSelectedState({ value: stateShortName })
        }

        if (oneStepCheckout) {
          queryParams = JSON.parse(AES.decrypt(oneStepCheckout, process.env.ENCRYPTION_KEY).toString(enc.Utf8))
        }


        const { attorney, locationState, trafficCounty, trafficCourt, winItPrice } = queryParams || {}
        if (
          attorney &&
          locationState &&
          trafficCounty &&
          trafficCourt &&
          winItPrice &&
          userFirstName &&
          userLastName &&
          userEmail &&
          userPhoneNumber
        ) {

          let attorneyQuote = null
          let sessionToken = ""
          let ticketObjectId = ""

          try {
            const data = yield TrafficTicketService.createTrafficTicketFromOneStepCheckout({
              attorney,
              locationState,
              trafficCounty,
              trafficCourt,
              winItPrice,
              signupSource: TrafficTicketService.SIGNUP_SOURCES.web,
              user: {
                userFirstName,
                userLastName,
                userPhoneNumber,
                userEmail: userEmail.toLowerCase(),
              },
            })

            attorneyQuote = data.attorneyQuotes[0] // todo: satisfy eslint-prefer-destructuring
            sessionToken = data.sessionToken
            ticketObjectId = data.objectId
            if (ticketObjectId) trackMetaBrowserLead(ticketObjectId)
          } catch (error) {
            console.error(error)
          }

          if (attorneyQuote && Object.keys(attorneyQuote).length) {
            targetStep = getStepFromSlug("payment-details", self.selectedState.available && !self.selectedState.isPQFlowEnabled)
            self.setIsOneStepCheckout(true)

            // TrafficQuote also has an "id" field - Can maybe be same as objectId? Not sure why we have both.
            if (!attorneyQuote.id) {
              attorneyQuote.id = attorneyQuote.objectId
            }

            user.changeKey("name", `${userFirstName} ${userLastName}`)
            user.changeKey("email", userEmail)
            user.changeKey("phoneNumber", userPhoneNumber)

            self.setQuotes([attorneyQuote])
            self.trafficTickets[0].selectQuote(attorneyQuote)
            self.trafficTickets[0].changeKey("objectId", ticketObjectId)

            if (sessionToken) {
              LocalStorage.set("sessionToken", sessionToken)
              yield authStore.sessionTokenLogin(sessionToken)
            } else { // User already exists, so we need to get them logged in:
              rootStore.auth.changeKey("email", userEmail)
              // If we don't want to rely on the user-info table, we can raise a modal as well:
              // rootStore.ui.setLoginModalShowing(true)
            }
          }
        }

        self.setStep(targetStep)
        if (targetStep.onEnter) yield targetStep.onEnter(self)
        self.initialized = true
      }
      self.changeStatusText(null)
      return true
    }),
    showToast(message, type) {
      const all = getRoot(self)
      all.ui.toasts.addToast({
        message,
        type
      })
    },
    loadImages: () => {
      self.trafficTickets.forEach(ticket => {
        if (ticket.persistedTicketImage) {
          ticket.persistedTicketImage.fetchAlreadyPersistedImage()
        }
        ticket.persistedEvidenceImages.forEach(img =>
          img.fetchAlreadyPersistedImage()
        )
      })
    },
    updateTickets: flow(function* updateTicketsGen(keys) {
      let saved = false
      try {
        const updatePromises = []
        self.trafficTickets.forEach(ticket => {
          if (ticket.isPersisted) {
            updatePromises.push(ticket.updateTicket(keys))
          }
        })
        yield Promise.all(updatePromises)
        saved = true
      } catch (error) {
        saved = false
        self.showErrorToast(`Issue saving tickets: ${error}`)
      }
      return saved
    }),
    changeStatusText(newText) {
      self.statusText = newText
    },
    // Get current step of the ticket based on steps validation. Note that we do not save court info until a after user info so this will boot users back to region info when they restart prior to filling out court info.
    getStepFromLoadedTicket() {
      const firstFailedValidation = getSubmissionSteps(self.selectedState.available).reduce(
          (target, fullStep) => {
          if (target) return target
          const validationErrors = self.validateStep(fullStep)
          const hasErrors = validationErrors && validationErrors.length > 0
          if (hasErrors || fullStep.order === 6) {
            self.trafficTickets.forEach(ticket => ticket.resetDisplayErrors())
            return fullStep
          }
        },
        null
      )
      return firstFailedValidation
    },
    // Show an error toast to the user
    showErrorToast(message) {
      const all = getParent(self)
      all.ui.toasts.addToast({
        message:
          message ||
          `Sorry something unexpected happened. Please try again or contact us for further support.`,
        type: 'ERROR'
      })
    },
    loadRegion: flow(function* loadRegionGen(regionObjectId) {
      if (!regionObjectId) return false
      self.setTrafficRegionsStatus(REQUEST_STATUSES.loading)
      try {
        const loadedRegion = yield TrafficRegionService.getByObjectId(
          regionObjectId
        )
        self.addTrafficRegions([loadedRegion])
        self.setTrafficRegionsStatus(REQUEST_STATUSES.complete)
      } catch (error) {
        if (process.env.WINIT_ENV !== 'test') {
          console.error(
            'error fetching a specific traffic regions for state',
            error
          )
        }
        self.showErrorToast(
          `Issue fetching a specific traffic region: ${error}`
        )
        self.setTrafficRegionsStatus(REQUEST_STATUSES.error)
      }
      return true
    }),
    loadViolations: flow(function* loadViolationsGen() {
      try {
        self.movingViolationService.requestReset()
        self.movingViolationService.requestStart()
        const rawViolations = yield TrafficTicketService.movingViolations(self.selectedState.shortName)
        const violations = rawViolations.map(sanitizeViolation)
        self.setMovingViolations(violations)
        self.movingViolationService.requestComplete()
      } catch (error) {
        if (process.env.WINIT_ENV !== 'test') {
          console.error('error fetching moving violations', error)
        }
        self.movingViolationService.requestError(error)
      }
    }),
    loadRegions: flow(function* loadRegionsGen() {
      try {
        self.setTrafficRegionsStatus(REQUEST_STATUSES.loading)
        const trafficRegions = yield TrafficRegionService.regionsForState(
          self.selectedState.shortName
        )
        self.addTrafficRegions(trafficRegions, true)
        self.setTrafficRegionsStatus(REQUEST_STATUSES.complete)
      } catch (error) {
        if (process.env.WINIT_ENV !== 'test') {
          console.error('error fetching traffic regions for state', error)
        }
        self.showErrorToast(`Issue fetching traffic regions: ${error}`)
        self.setTrafficRegionsStatus(REQUEST_STATUSES.error)
      }
    }),
    addTrafficRegions(trafficRegions, isAllRegions = false) {
      if (trafficRegions) {
        self.trafficRegionsLoadedAll = isAllRegions
        // if regions passed in are already in our regions array, replace the current value we have stored with new one
        const newRegionObjectIds = trafficRegions.map(region => region.objectId)
        const currentRegions = getSnapshot(self).trafficRegions
        const finalRegions = currentRegions
          .filter(region => !newRegionObjectIds.includes(region.objectId))
          .concat(trafficRegions)
        // set the new regions
        self.setRegionChoices(finalRegions)
        // setting regions before status is necessary to prevent skipping the step
        // self.setTrafficRegionsStatus(REQUEST_STATUSES.complete)
      }
    },
    addTrafficTicket(existingTicket = {}, sessionToken) {
      const originalLength = self.trafficTickets.length
      self.trafficTickets.push(existingTicket)
      const ticketJustAdded = self.trafficTickets[originalLength]
      if (existingTicket && existingTicket.ticketImage) {
        ticketJustAdded.persistedTicketImage.fetchAlreadyPersistedImage(
          sessionToken
        )
      }
      ticketJustAdded.checkAndFetchEvidenceImages()
      if (existingTicket && existingTicket.objectId) {
        const currentState = getSnapshot(ticketJustAdded)
        ticketJustAdded.setLastUpdate(currentState)
      }
    },
    removeTrafficTicket(fakeId) {
      self.trafficTickets = self.trafficTickets.filter(
        item => item.fakeId !== fakeId
      )
    },
    validateStep(stepOveride) {
      const currentFullStep = self.fullStep
      const fullStep = stepOveride || currentFullStep
      // if no errors return blank array
      if (!fullStep || !fullStep.validateKeys || !fullStep.validatationTarget)
        return []

      // get the object we should validate from current step
      const evalTarget = fullStep.validatationTarget(self)

      let displayErrors = []
      // if target is an array of object then iterate on each object with  keys to validate
      if (Array.isArray(evalTarget)) {
        evalTarget.forEach(target => {
          const errors = self.validateStepTargetKeys(
            target,
            fullStep.validateKeys
          )
          displayErrors.push(errors)
        })
        // merge multiple errors into a single array and return
        return displayErrors.reduce((allErrors, modelErrors) => {
          const results = allErrors.concat(modelErrors)
          return results
        }, [])
      }
      // if our target is a single object validate specific keys
      displayErrors = self.validateStepTargetKeys(
        evalTarget,
        fullStep.validateKeys
      )
      return displayErrors
    },
    asyncValidateStep: flow(function* () {
      // if no errors return blank array
      if (
        !self.fullStep
        || !self.fullStep.asyncValidateKeys
        || !self.fullStep.validatationTarget
      ) {
        return []
      }

      // get the object we should validate from current step
      const evalTarget = self.fullStep.validatationTarget(self)

      // if our target is a single object validate specific keys
      return yield self.asyncValidateStepTargetKeys(
        evalTarget,
        self.fullStep.asyncValidateKeys
      )
    }),
    // runs a validation function an object for a specified array of object keys.
    validateStepTargetKeys(target, keys) {
      // reset errors
      target.setDisplayErrors([])
      // reset dirty keys
      target.resetTouched()
      // build an array of keys with an error message
      const errors = keys
        .map(key => ({
          message: target.validateKey(key),
          target: key,
          fakeId: target.fakeId
        }))
        // remove items where validation return false (i.e. no error)
        .filter(err => err.message)
      // return and set array of errors
      target.setDisplayErrors(errors)
      return errors
    },

    asyncValidateStepTargetKeys: flow(function* (target, keys) {
      // build an array of keys with an error message
      const allErrors = yield Promise.all(keys.map(key => self.asyncValidateSingleKey(target, key)))
      const errors = allErrors.filter(err => err.message)

      // return and set array of errors
      target.addDisplayErrors(errors)
      return errors
    }),
    asyncValidateSingleKey: flow(function* (target, key) {
      return {
        message: yield target.asyncValidateKey(key),
        target: key,
        fakeId: target.fakeId
      }
    }),
    fetchFinancingPlan: flow(function* (financingPlanId, sessionToken) {
      try {
        const financingPlan = yield TrafficTicketService.getFinancingPlanDetails(financingPlanId, sessionToken)
        self.setSelectedFinancingPlan(financingPlan)
      } catch (error) {
        console.error("Error fetching financing plan:", error.message)
        throw error
      }
    }),
    /**
     * Changes the current step. Handles current step variable
     * @param {object} step A full step object
     */
    setStep(step) {
      self.step = step.slug
      TRACKING.traffic.trackCurrentStep(`${TRACKING_STEP_NAMES[step.slug]}`)
    },
    canDisplayBackButton() {
      return self.step !== "attorney" && self.step !== "violation"
    },
    getPathNameFlow() {
      const stateShortName = self.selectedState.shortName
      // get the state slug for the appropriate traffic submission flow url

      const urlSlug = Object.keys(TRAFFIC_STATES_URL_SLUGS).find((slug) => {
        return TRAFFIC_STATES_URL_SLUGS[slug].shortName === stateShortName
      })

      return `/${urlSlug}-traffic-tickets`
    },
    /**
     * Goes back to previous step and runs previous step on enter. does not call current exit.
     */
    previousStep: flow(function* previousStep() {
      self.clearCurrentStepErrors() // avoid showing an error from a component further down the history stack
      const prevStep = getPreviousStep(self.step, self.selectedState.available && !self.selectedState.isPQFlowEnabled)
      if (prevStep && prevStep.slug) {
        // if already saw attorneys and the user only has one attorney available, skip back two steps
        let targetStep = prevStep
        if (prevStep.slug === 'attorney' && self.quotes.length === 1) {
          targetStep = getPreviousStep(prevStep.slug, self.selectedState.available && !self.selectedState.isPQFlowEnabled)
        }
        self.setStep(targetStep)
        if (targetStep.onEnter) yield targetStep.onEnter(self)
      }
      return false
    }),
    /**
     * Prevents moving to next step if current step has errors. Runs current step on exit and runs next steps on enter.
     */
    nextStep: flow(function* nextStepGen() {
      self.clearCurrentStepErrors()
      // Check for errors on all tickets.
      const currentStepErrors = self.validateStep()
      // If we have errors do not proceed
      if (currentStepErrors && currentStepErrors.length >= 1) {
        currentStepErrors.forEach((error) => {
          self.currentStepErrors.push(error.message)
        })
        return false
      }

      const currentAsyncStepErrors = yield self.asyncValidateStep()
      // If we have errors do not proceed
      if (currentAsyncStepErrors && currentAsyncStepErrors.length >= 1) {
        const errorMessages = currentAsyncStepErrors.map(({ message }) => message)
        self.currentStepErrors.push(...errorMessages)

        return false
      }

      self.setIsLoading(true)

      // If step has onExit function run it
      const currentStep = self.fullStep
      if (currentStep && currentStep.onExit) {
        const allowExit = yield currentStep.onExit(self)
        // On exit returns true/false whether to allow continue
        if (!allowExit) {
          self.setIsLoading(false)
          return false
        }
      }

      if (self.nextStepConfig && self.nextStepConfig.slug) {
        const startedStep = self.step
        if (self.nextStepConfig.onEnter) yield self.nextStepConfig.onEnter(self)
        const endedStep = self.step

        // if on enter function changed steps already do not change step
        if (startedStep === endedStep) self.setStep(self.nextStepConfig)
      }
      return false
    }),
    clearCurrentStepErrors() {
      self.currentStepErrors = []
    },
    setIsLoading(bool) {
      self.isLoading = bool
    },
    setMobileMidstepCompleted(bool) {
      self.mobileMidstepCompleted = bool
    },
    setTrafficStates(statesArray) {
      self.trafficStates = statesArray
    },
    loadTrafficStates: flow(function* fetchStates() {
      try {
        self.setTrafficStatesStatus(REQUEST_STATUSES.loading)
        const trafficStates = yield TrafficStateService.getAll()
        console.log('Fetched traffic states:', trafficStates)
        self.setTrafficStates(trafficStates)
        self.setTrafficStatesStatus(REQUEST_STATUSES.complete)
      } catch (error) {
        self.showErrorToast(`Issue loading states: ${error}`)
        self.setTrafficStatesStatus(REQUEST_STATUSES.error)
      }
    }),
    setRegionChoices(regionsArray) {
      self.trafficRegions = regionsArray
        .sort((a, b) => {
          if (a.order < b.order) return -1
          if (a.order > b.order) return 1
          return 0
        })
        .map(item => TrafficRegion.create(item))
    },
    setCourtChoices(courtsArray) {
      self.trafficCourts = courtsArray
    },
    setCountyChoices(countiesArray) {
      self.trafficCounties = countiesArray
    },
    selectRegion(region, clearCourtsAndRegions=true) {
      self.trafficTickets.forEach(ticket => {
        ticket.setState(region.state)
        ticket.setTrafficRegion(region.objectId)
        if (clearCourtsAndRegions) {
          ticket.changeKey('trafficCourt', null)
          ticket.changeKey('trafficCounty', null)
        }
      })
      self.loadRegion(region.objectId)
    },
    /**
     * Changes the selected state
     * @param {object} newStateOption Input desired state object
     * @param {object.value} newStateOpton The uppercase short state name.
     */
    setSelectedState(newStateOption) {
      const target = self.trafficStates.filter(
        item => item.shortName === newStateOption.value
      )
      if (!target || !target.length) return false
      // Reset traffic regions on state change
      if (target[0].shortName !== self.selectedState.shortName) {
        self.trafficRegionsLoadedAll = false
        self.trafficRegions = []
        self.trafficRegionsStatus = REQUEST_STATUSES.init
      }
      self.selectedState = {
        shortName: target[0].shortName,
        longName: target[0].longName,
        available: target[0].available,
        isPQFlowEnabled: target[0].isPQFlowEnabled,
        prospect: target[0].prospect
      }
      self.trafficTickets.forEach(ticket => {
        ticket.changeKey('locationState', self.selectedState.shortName)
        ticket.changeKey('trafficRegion', undefined)
      })
      self.setIsStateAffiliatedWithWinitLaw(self.selectedState.shortName)
      return self.selectedState
    },
    setTrafficStatesStatus(status) {
      self.trafficStatesStatus = status
    },
    setTrafficRegionsStatus(status) {
      self.trafficRegionsStatus = status
    },
    setMovingViolations(movingViolations) {
      self.movingViolations = movingViolations
    },
    setSelectedViolation(violationOption) {
      self.trafficTickets[0].changeKey('violationName', violationOption.label)
      self.setSelectedViolationPoints(Number(violationOption.points))
      self.selectedMovingViolationId = violationOption.value
      self.selectedMovingViolationSlider = violationOption.slider
    },
    setSelectedViolationPoints(number) {
      self.trafficTickets[0].changeKey('points', number)
    },
    // Array of quotes from quote API
    setQuotes(quotes) {
      self.quotes = quotes
    },
    // The network status of fetching quotes
    setQuotesStatus(status) {
      self.quotesStatus = status
    },
    /**
     * Changes the screen to show the user
     * @param {string} val A SCREEN key
     */
    setScreen(val) {
      self.screen = val
    },
    /**
     * sets the current item to show on attorney loading screen.
     * @param {number} id Takes an item id number from the array.
     */
    setAttorneyFakeStatus(id) {
      self.attorneyFakeStatus = id
    },
    // This is the checkout function passed to the paymentStore at the checkout page.
    handleCheckout: flow(function* handleCheckoutGen() {
      // update the ticket with any final changes
      const FINAL_UPDATE_KEYS = [
        "status",
        "attorney",
        "attorneyPrice",
        "winItPrice",
        "basePrice",
        "platformFee",
        "caseProtectionActivated",
        "caseProtectionFee"
      ];
      const ticket = self.trafficTickets[0]
      const { objectId, ticketID } = ticket
      const root = getRoot(self)
      const { isACH } = root.payment
      const { user } = root.auth
      const { sessionToken } = user
      try {
        const updated = yield self.updateTickets(FINAL_UPDATE_KEYS)
        if (!updated) {
          throw new Error("Sorry there was an issue updating your ticket.")
        }
        let result
        if(self.isOneStepPaymentPlanCheckout) {
          if (isACH) {
            result = yield chargePaymentPlanDownPaymentACH(
              self.selectedFinancingPlan.objectId,
              root.payment.bankAccountPaymentMethod,
              sessionToken
            )
          } else {
            result = yield chargePaymentPlanDownPayment(self.selectedFinancingPlan, sessionToken)
            if (result && !result.includes("Charge success for payment plan DP")) {
              throw new Error("Issue charging payment plan downPayment")
            }
          }
        }
        else {
          result = yield chargeTrafficTicket(objectId, ticketID)
          if (result && !result.includes("Charge success for ticket")) {
            throw new Error("Issue charging ticket")
          }
        }
        self.setStep(getStepFromSlug("done", self.selectedState.available && !self.selectedState.isPQFlowEnabled))
        self.setScreen(SCREEN_KEYS.submittedTicket)
        TRACKING.traffic.submissionConversion(
          ticket.objectId,
          "traffic flow",
          self.selectedState && self.selectedState.shortName,
          ticket.winItPrice
        )
        trackMetaBrowserPurchase(ticket.objectId, ticket.winItPrice)
        return result
      } catch (error) {
        if (!isACH && [PAYMENT_PROVIDERS.cko, PAYMENT_PROVIDERS.evervault].includes(user.paymentProcessor.type)) {
          getRoot(self).payment.remountFlow()
        }
        console.error("error charging traffic ticket", error)
        self.showToast(error, "ERROR")
        return false
      }
    }),
    setIsOneStepCheckout(bool) {
      self.isOneStepCheckout = bool
    },
    setIsOneStepPaymentPlanCheckout(bool) {
      self.isOneStepPaymentPlanCheckout = bool
    },
    setSelectedFinancingPlan(financingPlan) {
      self.selectedFinancingPlan = financingPlan
    }
  }))
